import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpTrayIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ACCEPTED_EXTENSIONS, isAcceptedWorkbook } from '@/utils/gconUtils';

const FileDropZone = ({ label, description, files, onFiles, onRemove, id }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = e => {
    e.preventDefault();
    setDragging(false);
    const accepted = [...e.dataTransfer.files].filter(isAcceptedWorkbook);
    if (accepted.length) onFiles(accepted);
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold text-gray-700">{label}</p>
      <p className="text-xs text-gray-400">{description}</p>
      {files.map((file, i) => (
        <div
          key={`${file.name}-${i}`}
          className="border-2 border-dashed border-gray-200 rounded-lg p-3 flex items-center gap-3"
        >
          <DocumentIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
            <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-400"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div
        onDrop={handleDrop}
        onDragOver={e => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
        onClick={() => inputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 cursor-pointer
          transition-colors duration-150 ${files.length ? 'p-3' : 'p-6'}
          ${dragging ? 'border-info bg-blue-50' : 'border-gray-300 hover:border-teal-400'}
        `}
      >
        <ArrowUpTrayIcon className="w-6 h-6 text-gray-400" />
        <p className="text-sm text-gray-500">
          <span className="text-teal-500 font-medium">{files.length ? '+ Add another year' : 'Choose files'}</span> or
          drag & drop
        </p>
        <p className="text-xs text-gray-400">Select several years at once · .xlsx or .xlsm</p>
        <input
          ref={inputRef}
          id={id}
          type="file"
          multiple
          accept={ACCEPTED_EXTENSIONS.join(',')}
          className="sr-only"
          onChange={e => {
            const accepted = [...e.target.files].filter(isAcceptedWorkbook);
            if (accepted.length) onFiles(accepted);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
};

FileDropZone.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  onFiles: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  id: PropTypes.string,
};

export default FileDropZone;
